import React, { useState, useRef, useEffect } from "react";

export function Select({ value, onValueChange, children, className, ...props }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);
  const selectRef = useRef(null);

  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value);
    }
  }, [value]);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleClickOutside = (event) => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleValueChange = (newValue) => {
    setSelectedValue(newValue);
    if (onValueChange) {
      onValueChange(newValue);
    }
    setIsOpen(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={selectRef} className={`relative ${className || ""}`} {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          if (child.type === SelectTrigger) {
            return React.cloneElement(child, {
              isOpen,
              setIsOpen: handleToggle,
              value: selectedValue
            });
          } else if (child.type === SelectContent) {
            return React.cloneElement(child, {
              isOpen,
              children: React.Children.map(child.props.children, (contentChild) => {
                if (React.isValidElement(contentChild) && contentChild.type === SelectItem) {
                  return React.cloneElement(contentChild, {
                    onValueChange: handleValueChange,
                    selected: contentChild.props.value === selectedValue,
                    setIsOpen
                  });
                }
                return contentChild;
              })
            });
          }
          return child;
        }
        return child;
      })}
    </div>
  );
}

export function SelectTrigger({ children, isOpen, setIsOpen, value, className, ...props }) {
  return (
    <button
      type="button"
      onClick={setIsOpen}
      className={`flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className || ""}`}
      {...props}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === SelectValue) {
          return React.cloneElement(child, { value });
        }
        return child;
      })}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`ml-2 h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>
  );
}

export function SelectValue({ value, placeholder, ...props }) {
  return <span {...props}>{value || placeholder}</span>;
}

export function SelectContent({ children, isOpen, className, ...props }) {
  if (!isOpen) return null;

  return (
    <div
      className={`absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-white text-black shadow-md animate-in fade-in-80 mt-1 w-full ${className || ""}`}
      {...props}
    >
      <div className="p-1">{children}</div>
    </div>
  );
}

export function SelectItem({ children, value, onValueChange, selected, setIsOpen, className, ...props }) {
  const handleSelect = () => {
    if (onValueChange) {
      onValueChange(value);
    }
  };

  return (
    <div
      className={`relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 ${selected ? "bg-gray-200 text-black font-medium" : ""} ${className || ""}`}
      onClick={handleSelect}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        {selected && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        )}
      </span>
      <span className="truncate">{children}</span>
    </div>
  );
}
